import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Button, Typography } from 'antd'
import { FileProtectOutlined, StopOutlined } from '@ant-design/icons'
import employerService from 'services/employer'
import {
  normalizeUserFromLogin,
  signOut,
  updateAuthUser,
} from 'store/slices/authSlice'
import { isEmployerUserType } from 'constants/AuthConstant'
import {
  AGREEMENT_PROFILE_PATH,
  isAgreementRoute,
  mergeEmployerApiProfile,
  needsEmployerActiveAccount,
  needsEmployerAgreement,
  unwrapEmployerProfileResponse,
} from 'utils/employerAgreement'

const { Paragraph, Title } = Typography

/**
 * Employer access gate:
 * - Non-Active → block + sign out (never unlock app)
 * - Unsigned agreement → force agreement route + modal
 * Refreshes GET /employer/profile on mount so localStorage stays fresh.
 */
const EmployerAgreementGate = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user, type } = useSelector((state) => state.auth)
  const refreshedRef = useRef(false)

  const inactive = needsEmployerActiveAccount(user, type)
  const mustSign = !inactive && needsEmployerAgreement(user, type)
  const onAgreementPage = isAgreementRoute(location.pathname)

  useEffect(() => {
    if (!isEmployerUserType(type || user?.userType || user?.type)) return
    if (refreshedRef.current) return
    refreshedRef.current = true

    let cancelled = false
    const refresh = async () => {
      try {
        const response = await employerService.getProfile()
        if (cancelled) return
        const profile = unwrapEmployerProfileResponse(response)
        if (!profile) return
        const nextUser = normalizeUserFromLogin(
          mergeEmployerApiProfile(user, profile)
        )
        dispatch(updateAuthUser(nextUser))
      } catch (error) {
        console.log(error, 'employer-profile-refresh-err')
      }
    }

    refresh()
    return () => {
      cancelled = true
    }
    // Once per mount — keep session agreement/status fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, type])

  useEffect(() => {
    if (inactive) return
    if (!mustSign) return
    if (onAgreementPage) return
    navigate(AGREEMENT_PROFILE_PATH, { replace: true })
  }, [inactive, mustSign, onAgreementPage, navigate, location.pathname])

  if (inactive) {
    return (
      <>
        {children}
        <Modal
          open
          closable={false}
          maskClosable={false}
          keyboard={false}
          footer={[
            <Button
              key="out"
              type="primary"
              danger
              onClick={() => dispatch(signOut())}
            >
              Sign Out
            </Button>,
          ]}
        >
          <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
            <StopOutlined style={{ fontSize: 40, color: '#ff4d4f' }} />
            <Title level={4} style={{ marginTop: 16 }}>
              Account not active
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Your employer account is not active. The panel stays locked.
              Contact support if you need access.
            </Paragraph>
          </div>
        </Modal>
      </>
    )
  }

  if (!mustSign) {
    return children
  }

  return (
    <>
      {children}
      <Modal
        open={!onAgreementPage}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={[
          <Button
            key="sign"
            type="primary"
            onClick={() => navigate(AGREEMENT_PROFILE_PATH)}
          >
            Review &amp; Sign Agreement
          </Button>,
        ]}
      >
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <FileProtectOutlined style={{ fontSize: 40, color: '#3e79f7' }} />
          <Title level={4} style={{ marginTop: 16 }}>
            Agreement required
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Sign employer agreement before using the panel. Other pages stay
            locked until accepted.
          </Paragraph>
        </div>
      </Modal>
    </>
  )
}

export default EmployerAgreementGate
